import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './SuggestionsPanel.css';

interface Suggestion {
  id: string;
  name: string;
  type: string;
  reason: string;
  trustLevel: string;
}

interface SuggestionsPanelProps {
  onAddBeneficiary: (beneficiary: any) => void;
}

const SuggestionsPanel: React.FC<SuggestionsPanelProps> = ({ onAddBeneficiary }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const response = await fetch('/api/suggestions');
      const data = await response.json();
      setSuggestions(data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuggestion = (suggestion: Suggestion) => {
    const beneficiaryData = {
      name: suggestion.name,
      email: `contact@${suggestion.name.toLowerCase().replace(/\s+/g, '')}.com`,
      accountNumber: `****${Math.floor(1000 + Math.random() * 9000)}`,
      trustLevel: suggestion.trustLevel,
      discoveryMethod: suggestion.type
    };
    
    onAddBeneficiary(beneficiaryData);
  };

  if (loading) {
    return (
      <div className="suggestions-panel">
        <h3>Smart Suggestions</h3>
        <div className="suggestions-loading">
          <motion.div 
            className="loading-dots"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            Loading suggestions...
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="suggestions-panel"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="panel-header">
        <h3>Smart Suggestions</h3>
        <span className="suggestion-count">{suggestions.length}</span>
      </div>
      
      <div className="suggestions-list">
        {suggestions.map((suggestion, index) => (
          <motion.div
            key={suggestion.id}
            className="suggestion-item"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="suggestion-info">
              <h4 className="suggestion-name">{suggestion.name}</h4>
              <p className="suggestion-reason">{suggestion.reason}</p>
              <div className="suggestion-meta">
                <span className={`trust-badge ${suggestion.trustLevel}`}>
                  {suggestion.trustLevel}
                </span>
                <span className="suggestion-type">{suggestion.type}</span>
              </div>
            </div>
            
            <motion.button
              className="add-suggestion-btn"
              onClick={() => handleAddSuggestion(suggestion)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              +
            </motion.button>
          </motion.div>
        ))}
        
        {suggestions.length === 0 && (
          <div className="no-suggestions">
            <div className="no-suggestions-icon">🔍</div>
            <p>No suggestions available</p>
            <span>Check back later for personalized recommendations</span>
          </div>
        )}
      </div>
      
      <motion.button
        className="refresh-suggestions"
        onClick={fetchSuggestions}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        🔄 Refresh
      </motion.button>
    </motion.div>
  );
};

export default SuggestionsPanel;
